import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "../../lib/utils";

export interface NumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size" | "type"
> {
  value: number | null | undefined;
  /** Empty means missing (null), never 0. */
  onChange: (value: number | null) => void;
  /** Shown after the number, e.g. "kr/h", "h", "%". */
  unit?: string;
  size?: "md" | "lg" | "xl";
}

const SIZE = {
  md: "text-[20px]",
  lg: "text-[26px]",
  xl: "text-[44px] leading-none",
} as const;

/**
 * A number that drives a result: mono digits with a unit suffix. Empty shows
 * "—" and reports null, so a missing value is never read as 0. Props such as
 * id, name and aria-* go to the input, so it works inside a FormControl.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    { value, onChange, unit, size = "lg", className, ...rest },
    ref,
  ) {
    return (
      <span
        className={cn(
          "flex items-baseline gap-2 rounded-xl border border-caire-hairline bg-caire-surface px-4 py-2 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-caire-ink",
          className,
        )}
      >
        <input
          ref={ref}
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          placeholder="—"
          {...rest}
          value={value ?? ""}
          onChange={(event) =>
            onChange(
              event.target.value === "" ? null : Number(event.target.value),
            )
          }
          className={cn(
            "w-full min-w-0 border-0 bg-transparent p-0 font-mono font-semibold tabular-nums text-caire-ink outline-none placeholder:text-caire-muted",
            SIZE[size],
          )}
        />
        {unit ? (
          <span className="shrink-0 text-sm text-caire-muted">{unit}</span>
        ) : null}
      </span>
    );
  },
);
